/**
 * TopNavBar — Cloudflare-inspired context switcher in the header.
 *
 * Renders three dropdowns for company-scoped users:
 *   Company > Functionality > Period
 * Admin users see their existing country/global badge instead.
 */
import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Business as BusinessIcon,
  Check as CheckIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../stores/appStore';
import { COLORS, FUNCTIONALITIES, FUNCTIONALITY_MAP } from './layoutConstants';

/** Shared dropdown styling — light purple background, rounded corners. */
const SELECT_SX = {
  '& .MuiSelect-select': {
    py: 0.75,
    px: 1.5,
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 600,
    color: '#1E293B',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E0D4F5',
    borderRadius: '10px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.accentPurple,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.accentPurple,
    borderWidth: 2,
  },
  backgroundColor: '#F3EEFB',
} as const;

function Separator() {
  return (
    <ChevronRightIcon
      sx={{
        fontSize: 18,
        color: '#94A3B8',
        mx: { xs: 0.25, sm: 0.5 },
        flexShrink: 0,
      }}
    />
  );
}

// ─── Company Section ────────────────────────────────────────────────

function CompanySelector() {
  const user = useAuthStore((s) => s.user);
  const switchCompany = useAuthStore((s) => s.switchCompany);
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);

  if (!user) return null;

  const companies = user.companies ?? [];
  const isConsultant = user.role === 'consultant_user' && companies.length > 1;

  if (isConsultant && user.company_id) {
    const handleChange = async (e: SelectChangeEvent<string>) => {
      const newId = e.target.value;
      if (newId === user.company_id) return;
      setSwitching(true);
      try {
        await switchCompany(newId);
        queryClient.invalidateQueries();
      } finally {
        setSwitching(false);
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <BusinessIcon
          sx={{
            fontSize: 18,
            color: COLORS.accentPurple,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Select
          value={user.company_id}
          onChange={handleChange}
          disabled={switching}
          size="small"
          variant="outlined"
          sx={{ ...SELECT_SX, minWidth: { xs: 110, sm: 170 }, maxWidth: { xs: 160, sm: 260 } }}
          renderValue={(selected) => {
            if (switching) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: COLORS.accentPurple }} />
                  <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Switching...</Typography>
                </Box>
              );
            }
            const name = companies.find((c) => c.id === selected)?.name ?? selected;
            return (
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                {name}
              </Typography>
            );
          }}
        >
          {companies.map((c) => (
            <MenuItem
              key={c.id}
              value={c.id}
              sx={{
                fontSize: 13,
                fontWeight: c.id === user.company_id ? 700 : 400,
                color: c.id === user.company_id ? COLORS.accentPurple : '#1E293B',
                backgroundColor: c.id === user.company_id ? '#F3EEFB' : 'transparent',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              {c.name}
              {c.id === user.company_id && (
                <CheckIcon sx={{ fontSize: 16, ml: 1, color: COLORS.accentPurple }} />
              )}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  // Company user (single company) — static label badge
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: { xs: 1.5, sm: 2 },
        py: 0.75,
        borderRadius: '10px',
        backgroundColor: '#F3EEFB',
        border: '1px solid #E0D4F5',
        minWidth: 0,
      }}
    >
      <BusinessIcon
        sx={{
          fontSize: 18,
          color: COLORS.accentPurple,
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Typography
        noWrap
        sx={{
          fontSize: { xs: 12, sm: 13 },
          fontWeight: 600,
          color: '#1E293B',
          maxWidth: { xs: 120, sm: 200 },
        }}
      >
        {user.company_name ?? 'Company'}
      </Typography>
    </Box>
  );
}

// ─── Company Type Badge ─────────────────────────────────────────────

/** Color mapping for company business types. */
const BUSINESS_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  General: { bg: '#EDE9FE', color: '#7C3AED' },
  Life: { bg: '#E0F2FE', color: '#0284C7' },
  Composite: { bg: '#FFF7ED', color: '#C2410C' },
  Medical: { bg: '#ECFDF5', color: '#059669' },
};

function CompanyTypeBadge() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  // For consultants, derive from the selected company in the companies list
  let businessType: string | null | undefined = user.business_type;
  if (user.role === 'consultant_user' && user.companies?.length && user.company_id) {
    const selected = user.companies.find((c) => c.id === user.company_id);
    if (selected?.business_type) businessType = selected.business_type;
  }

  if (!businessType) return null;

  const label = businessType === 'General' ? 'General Insurance' : businessType + ' Insurance';
  const colors = BUSINESS_TYPE_COLORS[businessType] ?? BUSINESS_TYPE_COLORS.General;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        backgroundColor: colors.bg,
        color: colors.color,
        border: 'none',
        display: { xs: 'none', md: 'flex' },
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

// ─── Functionality Section ──────────────────────────────────────────

function FunctionalitySelector() {
  const user = useAuthStore((s) => s.user);
  const selectedFunctionality = useAppStore((s) => s.selectedFunctionality);
  const setSelectedFunctionality = useAppStore((s) => s.setSelectedFunctionality);
  const navigate = useNavigate();

  if (!user) return null;

  // Filter to functionalities assigned to this user
  const userFuncs = user.functionalities ?? [];
  const available = FUNCTIONALITIES.filter((f) => userFuncs.includes(f.id));

  // Fallback: if no functionalities assigned, show audit_workbook
  const displayList = available.length > 0 ? available : FUNCTIONALITIES.filter((f) => f.id === 'audit_workbook');

  const handleChange = (e: SelectChangeEvent<string>) => {
    const newFunc = e.target.value;
    if (newFunc === selectedFunctionality) return;
    setSelectedFunctionality(newFunc);
    const def = FUNCTIONALITY_MAP[newFunc];
    if (def) {
      navigate(def.defaultRoute);
    }
  };

  // If only one functionality, show as static label
  if (displayList.length <= 1) {
    const current = FUNCTIONALITY_MAP[selectedFunctionality] ?? FUNCTIONALITIES[0];
    return (
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 0.75,
          borderRadius: '10px',
          backgroundColor: '#F3EEFB',
          border: '1px solid #E0D4F5',
        }}
      >
        <Typography
          noWrap
          sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: '#1E293B' }}
        >
          {current.label}
        </Typography>
      </Box>
    );
  }

  return (
    <Select
      value={selectedFunctionality}
      onChange={handleChange}
      size="small"
      variant="outlined"
      sx={{ ...SELECT_SX, minWidth: { xs: 110, sm: 160 }, maxWidth: { xs: 160, sm: 220 } }}
      renderValue={(selected) => {
        const label = FUNCTIONALITY_MAP[selected]?.label ?? selected;
        return (
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
            {label}
          </Typography>
        );
      }}
    >
      {displayList.map((f) => (
        <MenuItem
          key={f.id}
          value={f.id}
          sx={{
            fontSize: 13,
            fontWeight: f.id === selectedFunctionality ? 700 : 400,
            color: f.id === selectedFunctionality ? COLORS.accentPurple : '#1E293B',
            backgroundColor: f.id === selectedFunctionality ? '#F3EEFB' : 'transparent',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: f.id === selectedFunctionality ? 700 : 400 }}>
              {f.label}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{f.description}</Typography>
          </Box>
          {f.id === selectedFunctionality && (
            <CheckIcon sx={{ fontSize: 16, ml: 1, color: COLORS.accentPurple }} />
          )}
        </MenuItem>
      ))}
    </Select>
  );
}

// ─── Period Section ─────────────────────────────────────────────────

function PeriodSelector() {
  const years = useAppStore((s) => s.years);
  const selectedYear = useAppStore((s) => s.selectedYear);
  const setSelectedYear = useAppStore((s) => s.setSelectedYear);

  if (years.length === 0) return null;

  const handleChange = (e: SelectChangeEvent<string>) => {
    setSelectedYear(Number(e.target.value));
  };

  // Single year — static label
  if (years.length === 1) {
    return (
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 0.75,
          borderRadius: '10px',
          backgroundColor: '#F3EEFB',
          border: '1px solid #E0D4F5',
        }}
      >
        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600, color: '#1E293B' }}>
          FY{years[0]}
        </Typography>
      </Box>
    );
  }

  return (
    <Select
      value={String(selectedYear)}
      onChange={handleChange}
      size="small"
      variant="outlined"
      sx={{ ...SELECT_SX, minWidth: { xs: 80, sm: 100 }, maxWidth: 140 }}
      renderValue={(selected) => (
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
          FY{selected}
        </Typography>
      )}
    >
      {years.map((y) => (
        <MenuItem
          key={y}
          value={String(y)}
          sx={{
            fontSize: 13,
            fontWeight: y === selectedYear ? 700 : 400,
            color: y === selectedYear ? COLORS.accentPurple : '#1E293B',
            backgroundColor: y === selectedYear ? '#F3EEFB' : 'transparent',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          FY{y}
          {y === selectedYear && (
            <CheckIcon sx={{ fontSize: 16, ml: 1, color: COLORS.accentPurple }} />
          )}
        </MenuItem>
      ))}
    </Select>
  );
}

// ─── Admin Badge (unchanged behavior) ───────────────────────────────

function AdminBadge() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const isSuperAdmin = user.role === 'super_admin';
  const isCountryAdmin = user.role === 'admin';

  if (isCountryAdmin && user.country_name) {
    return (
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 0.75,
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #F3EEFB 0%, #EEF2FF 100%)',
          border: '1px solid #E0D4F5',
        }}
      >
        {user.country_flag_emoji && (
          <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{user.country_flag_emoji}</Typography>
        )}
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
            {user.country_name}
          </Typography>
          <Typography
            sx={{ fontSize: 11, fontWeight: 600, color: COLORS.accentPurple, letterSpacing: '0.03em' }}
          >
            Country Administration
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isSuperAdmin) {
    return (
      <Box
        sx={{
          px: 2,
          py: 0.75,
          borderRadius: '20px',
          fontSize: 13,
          fontWeight: 600,
          backgroundColor: '#F3EEFB',
          color: COLORS.accentPurple,
        }}
      >
        Global Administration
      </Box>
    );
  }

  return null;
}

// ─── Main Export ─────────────────────────────────────────────────────

export default function TopNavBar() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const isAdmin = user.role === 'super_admin' || user.role === 'admin';

  if (isAdmin) {
    return <AdminBadge />;
  }

  // Company-scoped users: Company > Functionality > Period
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.25, sm: 0.5 },
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <CompanySelector />
      <CompanyTypeBadge />
      <Separator />
      <FunctionalitySelector />
      <Separator />
      <PeriodSelector />
    </Box>
  );
}
